import { useEffect, useRef } from 'react'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { useDocumentIdle } from 'shared/lib/useDocumentIdle'
import { fetchPlaces } from '../api/placesApi'
import { placesKeys } from './query-keys'

/**
 * Polling cadence.
 *
 * Desk status only moves when a colleague sits down, so a fixed fast interval
 * spent the whole day re-fetching 133 desks to learn nothing. The cadence now
 * follows what the user is doing:
 *
 * - a desk card is open -> CONTENDED, because the button on that card is a
 *   promise: a status that goes stale there turns into a failed booking;
 * - map open and in use -> STEADY;
 * - untouched for IDLE_AFTER -> stopped, until the next interaction, tab focus
 *   or manual refresh.
 */
const CONTENDED_POLL_INTERVAL_MS = 15_000
const STEADY_POLL_INTERVAL_MS = 60_000
const IDLE_AFTER_MS = 5 * 60_000

/** Shorter than the 60s client default so returning to the app refetches. */
const PLACES_STALE_TIME_MS = 30_000

export function placesQueryOptions() {
  return queryOptions({
    queryKey: placesKeys.all,
    queryFn: ({ signal }) => fetchPlaces({}, { signal }),
    staleTime: PLACES_STALE_TIME_MS,
    networkMode: 'online',
  })
}

export interface UsePlacesQueryOptions {
  enabled?: boolean
  /**
   * A desk card is open. Raises the cadence for as long as it stays open, so
   * the status and the booking button on it keep matching the server.
   */
  isDeskCardOpen?: boolean
}

export function usePlacesQuery({
  enabled = true,
  isDeskCardOpen = false,
}: UsePlacesQueryOptions = {}) {
  const isIdle = useDocumentIdle(IDLE_AFTER_MS)

  const query = useQuery({
    ...placesQueryOptions(),
    enabled,
    // Coming back to a backgrounded webview should show today's map, not the
    // one from whenever it was last on screen.
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    refetchInterval: isIdle
      ? false
      : isDeskCardOpen
        ? CONTENDED_POLL_INTERVAL_MS
        : STEADY_POLL_INTERVAL_MS,
  })

  const { refetch } = query
  const wasIdleRef = useRef(isIdle)

  useEffect(() => {
    const wasIdle = wasIdleRef.current
    wasIdleRef.current = isIdle

    // Restarting the interval schedules its first tick a full interval away, so
    // without this the map the user just came back to stays minutes old.
    if (wasIdle && !isIdle && enabled) {
      void refetch()
    }
  }, [enabled, isIdle, refetch])

  return query
}
